import { UserSession } from '../user-session';
import { IdentityStorageType, Storage } from '../storage';
import { Transport, TransportTypeOrOptions } from '../transport';
import { IConfig } from './core-config';
import { ElementInteractionsOptions } from '../element-interactions';
import { FormInteractionsOptions } from '../form-interactions';
import { PageTrackingOptions } from '../page-view-tracking';
import { NetworkTrackingOptions } from '../network-tracking';
import { FrustrationInteractionsOptions } from '../frustration-interactions';
import { PerformanceTrackingOptions } from '../performance-tracking';
import { IDiagnosticsClient } from '../../diagnostics/diagnostics-client';
import { IRemoteConfigClient } from '../../remote-config/remote-config';
import { CustomEnrichmentOptions } from '../custom-enrichment';

export interface BrowserConfig extends ExternalBrowserConfig, InternalBrowserConfig {}

export interface ExternalBrowserConfig extends IConfig {
  /**
   * An app version for events tracked. This can be the version of your application.
   * @defaultValue `undefined`
   */
  appVersion?: string;
  /**
   * @deprecated This property is deprecated and will be removed in future versions. Please migrate to using `autocapture` instead.
   * The default event tracking configuration.
   * See {@link https://www.docs.developers.amplitude.com/data/sdks/browser-2/#tracking-default-events}.
   * @defaultValue `true`
   */
  defaultTracking?: boolean | DefaultTrackingOptions;
  /**
   * The configurations for auto-captured events.
   * See {@link https://www.docs.developers.amplitude.com/data/sdks/browser-2/autocapture/}.
   */
  autocapture?: boolean | AutocaptureOptions;
  /**
   * The configurations for custom enrichment.
   * @defaultValue `false`
   */
  customEnrichment?: boolean | CustomEnrichmentOptions;
  /**
   * The identifier for the device running your application.
   * @defaultValue `UUID()`
   */
  deviceId?: string;
  /**
   * Configuration for cookie.
   */
  cookieOptions?: CookieOptions;
  /**
   * The storage for user identify.
   * @defaultValue `"cookie"`
   */
  identityStorage?: IdentityStorageType;
  /**
   * The partner identifier.
   * Amplitude requires the customer who built an event ingestion integration to add the partner identifier to partner_id.
   * @defaultValue `undefined`
   */
  partnerId?: string;
  /**
   * The custom Session ID for the current session.
   * @defaultValue `timestamp`
   */
  sessionId?: number;
  /**
   * The period of inactivity from the last tracked event before a session expires in milliseconds.
   * @defaultValue `1,800,000` (30 minutes)
   */
  sessionTimeout: number;
  /**
   * The configurations for tracking additional properties.
   * See {@link https://www.docs.developers.amplitude.com/data/sdks/browser-2/#optional-tracking}.
   */
  trackingOptions: TrackingOptions;
  /**
   * Network transport mechanism used to send events.
   * @defaultValue `"fetch"`
   */
  transport?: TransportTypeOrOptions;
  /**
   * The identifier for the user being tracked.
   * @defaultValue `undefined`
   */
  userId?: string;
  /**
   * User's Nth instance of performing a default Page Viewed event within a session.
   * Used for landing page analysis.
   */
  pageCounter?: number;
  /**
   * Whether to fetch remote configuration. The remote configuration can be updated in the Amplitude platform here:
   * https://app.amplitude.com/data/amplitude/{your_org_name}/settings/autocapture
   * @defaultValue `true`
   * @deprecated This property is deprecated and will be removed in future versions. Please use `remoteConfig.fetchRemoteConfig` instead.
   */
  fetchRemoteConfig?: boolean;
  /**
   * Remote configuration options.
   * @defaultValue `undefined`
   */
  remoteConfig?: RemoteConfigOptions;
  /**
   * Captures network requests and responses.
   * @defaultValue `undefined`
   * @deprecated use autocapture.networkTracking instead
   */
  networkTrackingOptions?: NetworkTrackingOptions;
  /**
   * Whether to enable diagnostics.
   * @defaultValue `true`
   */
  enableDiagnostics?: boolean;
}

interface InternalBrowserConfig {
  cookieStorage: Storage<UserSession>;
  lastEventTime?: number;
  lastEventId?: number;
  transportProvider: Transport;
  version?: string;
  diagnosticsSampleRate?: number;
  diagnosticsClient?: IDiagnosticsClient;
  remoteConfigClient?: IRemoteConfigClient;
  deferredSessionId?: number;
  topLevelDomain?: string;
}

/**
 * @deprecated This interface is deprecated and will be removed in future versions. Please migrate to using `AutocaptureOptions` instead.
 */
export interface DefaultTrackingOptions {
  /**
   * Enables/disables marketing attribution tracking or config with detailed attribution options.
   * @defaultValue `true`
   */
  attribution?: boolean | AttributionOptions;
  /**
   * Enables/disables form downloads tracking.
   * @defaultValue `true`
   */
  fileDownloads?: boolean;
  /**
   * Enables/disables form interaction tracking or configures with detailed options.
   * @defaultValue `true`
   */
  formInteractions?: boolean | FormInteractionsOptions;
  /**
   * Enables/disables default page view tracking.
   * @defaultValue `true`
   */
  pageViews?: boolean | PageTrackingOptions;
  /**
   * Enables/disables session tracking.
   * @defaultValue `true`
   */
  sessions?: boolean;
}

export interface AutocaptureOptions {
  /**
   * Enables/disables marketing attribution tracking or config with detailed attribution options.
   * @defaultValue `true`
   */
  attribution?: boolean | AttributionOptions;
  /**
   * Enables/disables form downloads tracking.
   * @defaultValue `true`
   */
  fileDownloads?: boolean;
  /**
   * Enables/disables form interaction tracking or configures with detailed options.
   * @defaultValue `true`
   */
  formInteractions?: boolean | FormInteractionsOptions;
  /**
   * Enables/disables default page view tracking.
   * @defaultValue `true`
   */
  pageViews?: boolean | PageTrackingOptions;
  /**
   * Enables/disables session tracking.
   * @defaultValue `true`
   */
  sessions?: boolean;
  /**
   * Enables/disables user interactions tracking.
   * @defaultValue `false`
   */
  elementInteractions?: boolean | ElementInteractionsOptions;
  /**
   * Enables/disables frustration interactions tracking.
   * @defaultValue `false`
   */
  frustrationInteractions?: boolean | FrustrationInteractionsOptions;
  /**
   * Enables/disables network request tracking or config with detailed network tracking options.
   * @defaultValue `false`
   */
  networkTracking?: boolean | NetworkTrackingOptions;
  /**
   * Enables/disables web vitals tracking.
   * @defaultValue `false`
   */
  webVitals?: boolean;
  /**
   * Enables/disables performance tracking.
   * @defaultValue `false`
   */
  performanceTracking?: boolean | PerformanceTrackingOptions;
  /**
   * Enables/disables page url enrichment.
   * @defaultValue `true`
   * @experimental This feature is experimental and may not be stable
   */
  pageUrlEnrichment?: boolean;
}

export interface TrackingOptions {
  /**
   * Enables/disables ip address tracking.
   * @defaultValue `true`
   */
  ipAddress?: boolean;
  /**
   * Enables/disables language tracking.
   * @defaultValue `true`
   */
  language?: boolean;
  /**
   * Enables/disables plantform tracking.
   * @defaultValue `true`
   */
  platform?: boolean;
}

export type TrackingMethod = 'userProperty' | 'eventProperty';

export interface AttributionOptions {
  /**
   * The rules to determine which referrers are excluded from being tracked as traffic source.
   * Applies only to `userProperty` tracking.
   * Ignored by `eventProperty` tracking.
   * @defaultValue `[/your-domain\.com$/]`
   */
  excludeReferrers?: (string | RegExp)[];
  /**
   * Exclude internal referrers from campaign attribution.
   * (a referrer is 'internal' if it is on the same domain as the current page)
   * Applies only to `userProperty` tracking.
   * Ignored by `eventProperty` tracking.
   * @defaultValue `false`
   */
  excludeInternalReferrers?: true | false | ExcludeInternalReferrersOptions;
  /**
   * The value to represent undefined/no initial campaign parameter for first-touch attribution.
   * Applies only to `userProperty` tracking.
   * Ignored by `eventProperty` tracking.
   * @defaultValue `"EMPTY"`
   */
  initialEmptyValue?: string;
  /**
   * The flag of if Amplitude to start a new session if any campaign parameter changes.
   * Applies only to `userProperty` tracking.
   * Ignored by `eventProperty` tracking.
   * @defaultValue `false`
   */
  resetSessionOnNewCampaign?: boolean;
  /**
   * The attribution persistence strategy for campaign parameters.
   * Provide a single method to enable one strategy, or an array to enable multiple methods at the same time.
   * When multiple methods are provided, each enabled method runs independently and their behaviors are combined.
   * For example, `['userProperty', 'eventProperty']` updates user properties and also attaches campaign params
   * to event properties.
   * @experimental this feature is experimental and may not be stable
   * @defaultValue `"userProperty"`
   */
  trackingMethod?: TrackingMethod | TrackingMethod[];
  /**
   * Fires an `[Amplitude] Attribution` event as a heartbeat on every page view,
   * such as on page load and SPA URL changes.
   * Applies only to `eventProperty` tracking.
   * Ignored by `userProperty` tracking.
   * @experimental this feature is experimental and may not be stable
   * @defaultValue `false`
   */
  fallbackAttributionEvent?: boolean;
}

export const EXCLUDE_INTERNAL_REFERRERS_CONDITIONS = {
  always: 'always',
  ifEmptyCampaign: 'ifEmptyCampaign',
} as const;

type ExcludeInternalReferrersCondition =
  (typeof EXCLUDE_INTERNAL_REFERRERS_CONDITIONS)[keyof typeof EXCLUDE_INTERNAL_REFERRERS_CONDITIONS];

export interface ExcludeInternalReferrersOptions {
  /*
   * The condition on which to exclude internal referrers for campaign attribution.
   * @defaultValue `"always"`
   */
  condition?: ExcludeInternalReferrersCondition;
}

export interface RemoteConfigOptions {
  /**
   * Whether to fetch remote configuration. The remote configuration can be updated in the Amplitude platform here:
   * https://app.amplitude.com/data/amplitude/{your_org_name}/settings/autocapture
   * @defaultValue `true`
   */
  fetchRemoteConfig?: boolean;
  /**
   * Custom server URL for remote configuration requests. If not provided, defaults to the standard
   * Amplitude remote config endpoint based on serverZone (US or EU).
   * Use this to proxy remote config requests through your own server.
   * @defaultValue `undefined` (uses standard Amplitude remote config endpoint)
   */
  serverUrl?: string;
}

export interface CookieOptions {
  /**
   * The domain property of cookies created.
   * @defaultValue `Your top level domain`
   */
  domain?: string;
  /**
   * The expiration of cookies created in days.
   * @defaultValue `365`
   */
  expiration?: number;
  /**
   * How cookies are sent with cross-site requests.
   * @defaultValue `"Lax"`
   */
  sameSite?: 'Strict' | 'Lax' | 'None';
  /**
   * The flag of if send cookies over secure protocols.
   * @defaultValue `false`
   */
  secure?: boolean;
  /**
   * The flag of if upgrade the cookies created by maintenance Browser SDK.
   * @defaultValue `true`
   */
  upgrade?: boolean;
}

type HiddenOptions = 'apiKey' | 'transportProvider' | 'requestMetadata';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface BrowserOptions extends Omit<Partial<ExternalBrowserConfig>, HiddenOptions> {}
